import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { AuthContext } from './AuthContext';
import type { User, SignupData, AppleUserData, AuthProvider } from '../../types/auth';
import * as authApi from '../../api/auth';
import * as usersApi from '../../api/users';
import {
  setAuthToken,
  setLogoutCallback,
  setRefreshTokenCallback,
  setIsLoggingOut,
} from '../../api/index';
import { clearSelectedBusiness } from '../../utils/businessStorage';
import posthog from 'posthog-js';

interface AuthContextProviderProps {
  children: ReactNode;
}

// Storage keys
const STORAGE_KEYS = {
  TOKEN: '@auth_token',
  REFRESH_TOKEN: '@auth_refresh_token',
  USER: '@auth_user',
  PROVIDER: '@auth_provider',
};

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Clear all auth data from state and localStorage
   */
  const clearAuthData = useCallback(() => {
    setUser(null);
    setToken(null);
    setAuthToken(null);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.PROVIDER);
    // Clear shared login indicator cookie on parent domain
    document.cookie = 'easyqr_logged_in=; domain=.easyqr.menu; path=/; max-age=0; SameSite=Lax; Secure';
    // Clear selected business when logging out
    clearSelectedBusiness();
  }, []);

  /**
   * Store auth data in localStorage and state
   */
  const storeAuthData = useCallback(
    async (accessToken: string, refreshToken: string, provider: AuthProvider) => {
      // Store tokens
      localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(STORAGE_KEYS.PROVIDER, provider);

      // Remember last social login method (persists across logout for login page hint)
      if (provider === 'google' || provider === 'apple') {
        localStorage.setItem('@last_social_provider', provider);
      } else {
        localStorage.removeItem('@last_social_provider');
      }

      // Set axios auth header
      setAuthToken(accessToken);
      setToken(accessToken);

      // Set shared login indicator cookie on parent domain (readable by easyqr.menu)
      document.cookie = 'easyqr_logged_in=1; domain=.easyqr.menu; path=/; max-age=2592000; SameSite=Lax; Secure';

      // Fetch and store user data
      try {
        const userData = await usersApi.getCurrentUser();
        setUser(userData);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        throw error;
      }
    },
    []
  );

  /**
   * Refresh the access token using the refresh token
   */
  const handleRefreshToken = useCallback(async (): Promise<string> => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authApi.refreshToken(refreshToken);

      // Store new tokens
      localStorage.setItem(STORAGE_KEYS.TOKEN, response.access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token);

      // Update axios auth header
      setAuthToken(response.access_token);
      setToken(response.access_token);

      return response.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }, []);

  /**
   * Logout the user
   */
  const logout = useCallback(async () => {
    try {
      setIsLoggingOut(true);

      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      // Call logout endpoint if we have a refresh token
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }

      posthog.capture('user_logged_out');
      posthog.reset();

      // Clear all auth data
      clearAuthData();
    } catch (error) {
      console.error('Logout error:', error);
      // Clear data anyway even if API call fails
      clearAuthData();
    } finally {
      setIsLoggingOut(false);
    }
  }, [clearAuthData]);

  /**
   * Load stored auth data on app start
   */
  const loadStoredAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

      if (storedToken && storedUser) {
        // Set axios auth header
        setAuthToken(storedToken);
        setToken(storedToken);

        // Parse and set user data
        const userData = JSON.parse(storedUser);
        setUser(userData);

        // Fetch fresh user data in background
        try {
          const freshUserData = await usersApi.getCurrentUser();
          setUser(freshUserData);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(freshUserData));
          // Ensure the shared login cookie is set for already-logged-in users
          document.cookie = 'easyqr_logged_in=1; domain=.easyqr.menu; path=/; max-age=2592000; SameSite=Lax; Secure';
        } catch (error) {
          // If token is invalid, clear auth data
          console.error('Failed to fetch user data on load:', error);
          clearAuthData();
        }
      }
    } catch (error) {
      console.error('Error loading stored auth:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  }, [clearAuthData]);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await authApi.login(email, password);
        await storeAuthData(response.access_token, response.refresh_token, 'email');
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      }
    },
    [storeAuthData]
  );

  /**
   * Sign up with email and password
   */
  const signup = useCallback(async (data: SignupData) => {
    try {
      await authApi.register(data);
      // Note: User needs to verify email before logging in
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }, []);

  /**
   * Login with pre-obtained tokens (e.g. from accepting a staff invitation)
   */
  const loginWithTokens = useCallback(
    async (accessToken: string, refreshToken: string) => {
      try {
        await storeAuthData(accessToken, refreshToken, 'email');
      } catch (error) {
        console.error('Login with tokens error:', error);
        throw error;
      }
    },
    [storeAuthData]
  );

  /**
   * Login with OAuth provider (Google or Apple)
   */
  const loginWithOAuth = useCallback(
    async (provider: 'google' | 'apple', idToken: string, userData?: AppleUserData) => {
      try {
        let response;

        if (provider === 'google') {
          response = await authApi.loginWithGoogle(idToken);
        } else {
          response = await authApi.loginWithApple(idToken, userData);
        }

        await storeAuthData(response.access_token, response.refresh_token, provider);
      } catch (error) {
        console.error('OAuth login error:', error);
        throw error;
      }
    },
    [storeAuthData]
  );

  /**
   * Update user profile
   */
  const updateUser = useCallback(async (data: Partial<User>) => {
    try {
      const updatedUser = await usersApi.updateCurrentUser(data);
      setUser(updatedUser);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }, []);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const userData = await usersApi.getCurrentUser();
      setUser(userData);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    } catch (error) {
      console.error('Refresh user error:', error);
      throw error;
    }
  }, []);

  // Set up callbacks for axios interceptor
  useEffect(() => {
    setLogoutCallback(logout);
    setRefreshTokenCallback(handleRefreshToken);

    return () => {
      setLogoutCallback(null);
      setRefreshTokenCallback(null);
    };
  }, [logout, handleRefreshToken]);

  // Load stored auth on mount
  useEffect(() => {
    loadStoredAuth();
  }, [loadStoredAuth]);

  const value = {
    user,
    token,
    isLoading,
    login,
    signup,
    loginWithOAuth,
    loginWithTokens,
    logout,
    clearAuthData,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
